/**
 * Use cases from the nexus manual (Section 5).
 * Each use case includes a description, step-by-step instructions,
 * and a filter preset that can be applied to reproduce the scenario.
 */

const useCases = [
  {
    id: 'network-density',
    title: 'Characterizing Your Network in One Number',
    subtitle: 'Density as a single-metric snapshot',
    description:
      'If you need a single metric to describe the overall nature of your academic-industrial collaboration ecosystem, look at Density. With a density of 1.3% (0.013), the network is classified as "sparse" — a landscape of specific, targeted collaborations formed around niche expertise rather than general proximity.',
    steps: [
      'Set the Time filter to encompass the full range.',
      'Click Export to download the ZIP archive.',
      'Open the Data folder in the archive — Network Statistics includes the Density metric.'
    ],
    filterPreset: null, // This use case is about exporting, no specific filter preset needed beyond time range
    actionLabel: 'Set Full Time Range',
    actionType: 'setCycleRange'
  },
  {
    id: 'bridge-builders',
    title: 'Identifying Your Network\'s "Bridge Builders"',
    subtitle: 'Betweenness Centrality reveals the gatekeepers',
    description:
      'If you had to name the one entity that keeps your entire ecosystem connected — without whom the network would fragment into isolated islands — who would it be? Look at Betweenness Centrality. Unlike nodes with many connections, high-betweenness nodes sit strategically on paths between different groups. Across all time periods, the bridge builder for the academic-industrial collaboration network has been the beamline BALDER (0.181).',
    steps: [
      'Set the Time filter to encompass the full range.',
      'Click Export to download the ZIP archive.',
      'Open the Data folder — Network Statistics includes Betweenness Centrality rankings.'
    ],
    filterPreset: null,
    actionLabel: 'Set Full Time Range',
    actionType: 'setCycleRange'
  },
  {
    id: 'top-collaborator',
    title: 'Who Is Your Top Collaborator?',
    subtitle: 'Finding the most frequent company partner',
    description:
      'Looking across your entire history — from the first call Fall 2018 to today — which single company stands out as your most frequent collaborator? Tetra Pak, measuring at ForMAX. By filtering to only companies with accepted/finished proposals and maximizing the connection strength, the strongest partnership surfaces immediately.',
    steps: [
      'Set the Time filter to encompass the full range.',
      'Select "Company" in the Org Type filter.',
      'Select "Accepted" and "Finished" in the Status filter.',
      'Go to Graph Settings (⚙️) and move Connection Strength to maximum.',
      'Click on either node to see the Node Details — 10 proposals accepted and/or finished.'
    ],
    filterPreset: {
      orgType: ['Company'],
      status: ['Accepted', 'Finished'],
      industry: [],
      topics: [],
      organisation: [],
      collaborationType: [],
      unspecifiedMode: 'on'
    },
    connectionStrength: 'max',
    actionLabel: 'Load This Example',
    actionType: 'applyFilters'
  },
  {
    id: 'unspecified-partners',
    title: 'Turning "Unspecified" Partners into Real Opportunities',
    subtitle: 'Preparing for a targeted industry workshop',
    description:
      'Imagine preparing for an annual workshop focused on the Swedish Forest Industry. Your goal is to increase industrial utilization of forest-related capacities. You suspect many academic users are already collaborating with forest companies but have not explicitly named them. Using the Unspecified toggle, you can surface proposals that mention an existing collaboration but do not name the company — turning "unspecified" data into a concrete list of research leads to follow up on.',
    steps: [
      'Set the Time filter to encompass all time periods.',
      'Select "Forestry & Bio-Based Materials" in the Industry filter.',
      'Select "Accepted" and "Finished" in the Status filter.',
      'Go to Strategic Fine-Tuning — set Unspecified to "ONLY".',
      'Select "Actual" in Collaboration Type.',
      'The resulting network shows proposals with unnamed but real industrial partners.',
      'Click on Node Details to see proposal IDs — look up the PI in DUO for follow-up.'
    ],
    filterPreset: {
      industry: ['Forestry & Bio-Based Materials'],
      status: ['Accepted', 'Finished'],
      orgType: [],
      topics: [],
      organisation: [],
      collaborationType: ['actual'],
      unspecifiedMode: 'only'
    },
    actionLabel: 'Load This Example',
    actionType: 'applyFilters'
  }
];

export default useCases;
